import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { CustomData } from '@core/models/entities/implementations/custom-data';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';
import {
  GeoJsonPolygon,
  holeRings,
  isDrawablePolygon,
  outerRing,
  ringToGeoLocations
} from '@core/utils/geo-polygon.utils';

import {
  GaliaArea,
  GeoAreaModification
} from '@features/components/visualization/mapbox/models/galia-area';

export type UserGeoAreaData =
  | { kind: 'GeoJson'; geometry: GeoJsonPolygon }
  | { kind: 'PostalCodeMatch'; patterns: string[] };

export interface UserGeographicAreaV1Model {
  uid: string;
  name: string | null;
  data: UserGeoAreaData;
  custom_data: CustomData;
}

const AREA_COLOR = '#0f6b73';

export function isGeoJsonAreaData(
  data: UserGeoAreaData
): data is { kind: 'GeoJson'; geometry: GeoJsonPolygon } {
  return data?.kind === 'GeoJson';
}

export function buildGeographicAreas(raw: UserGeographicAreaV1Model): GaliaArea[] {
  if (!raw?.data || !isGeoJsonAreaData(raw.data) || !raw.data.geometry) {
    return [];
  }

  // geometry is unvalidated serde_json::Value on the backend; a truncated or non-Polygon
  // payload yields no usable ring and must not take down the whole visualization.
  const ring = outerRing(raw.data.geometry);
  if (ring.length === 0) {
    return [];
  }

  return [
    {
      uid: raw.uid,
      name: raw.name ?? raw.uid,
      ring: ringToGeoLocations(ring),
      holes: holeRings(raw.data.geometry).map(ringToGeoLocations),
      color: AREA_COLOR
    }
  ];
}

/**
 * Lets a zone's boundary be reshaped on the map it is already drawn on. Offered only for a
 * geometry Draw can seed: opening a session on an unusable one would show an empty canvas
 * and then save that emptiness over the stored value.
 */
export function buildGeographicAreaModifications(
  raw: UserGeographicAreaV1Model
): GeoAreaModification[] {
  if (!raw?.data || !isGeoJsonAreaData(raw.data) || !raw.data.geometry) {
    return [];
  }
  if (!isDrawablePolygon(raw.data.geometry)) {
    return [];
  }

  return [
    {
      uid: raw.uid,
      name: raw.name ?? raw.uid,
      entityType: 'geographic_area',
      entityTypeVersion: 'UserGeographicArea',
      geometry: raw.data.geometry,
      withGeometry: (geometry: GeoJsonPolygon) => ({
        uid: raw.uid,
        data: { kind: 'GeoJson', geometry }
      })
    }
  ];
}

export function registerGeographicAreaExtensions(
  registry: GenericEntityRegistryService
): void {
  const overrides: EntityCfgOverrides<UserGeographicAreaV1Model> = {
    customVisualizations: new Set(['map'])
  };
  registry.registerOverrides<UserGeographicAreaV1Model>('UserGeographicArea', overrides);
  registry.registerVisualizationBuilders<UserGeographicAreaV1Model>('UserGeographicArea', {
    buildAreas: buildGeographicAreas,
    buildAreaModifications: buildGeographicAreaModifications
  });
}
